const CREDIT_CARD = 'credit_card';
const PIX = 'pix';

/**
 * Taxas da plataforma, espelhando `config/fees.php` do backend.
 *
 * Valem só enquanto o evento não existe: sem slug não há o que perguntar à
 * API, e um evento que ainda vai nascer não tem taxa negociada. Assim que há
 * slug, quem manda é a resposta de `/events/detail/{slug}/fees` — nunca use
 * estes números para tapar o buraco de uma requisição que falhou, porque um
 * evento negociado a 5% exibiria 10% com cara de valor correto.
 */
export const PLATFORM_DEFAULT_FEE = { rate: 0.08, pixRate: 0.06, minimum: 2.5 };

/**
 * Abaixo deste preço, a taxa é o mínimo fixo, não a porcentagem — mesmo que a
 * porcentagem desse menos. Espelha `config('fees.flat_fee_threshold')` do
 * backend; é um corte deliberado, não o ponto onde a % ultrapassa o mínimo,
 * então não recalcule isto a partir da taxa.
 */
export const FLAT_FEE_THRESHOLD = 20;

/**
 * A API entrega as taxas como fração: 0.085 é 8,5%, nunca 8.5.
 *
 * A conversão mora só aqui de propósito. Espalhar `* 100` pelas telas é
 * exatamente como se erra por 100x, e a validação do backend (`between:0,1`)
 * recusa qualquer valor em porcentagem que escape daqui.
 */
const serviceFee = {
    METHODS: { CREDIT_CARD, PIX },

    /** 0.085 -> 8.5 */
    toPercent: (rate) => {
        const value = Number(rate);
        return Number.isFinite(value) ? value * 100 : 0;
    },

    /** 8,5 -> 0.085 — aceita vírgula, que é o que o usuário digita */
    fromPercent: (percent) => {
        const value = Number(String(percent ?? '').replace(',', '.'));
        return Number.isFinite(value) ? value / 100 : 0;
    },

    /** 0.085 -> '8,5%'  |  0.1 -> '10%' */
    formatPercent: (rate, { decimals = 2 } = {}) => {
        const fixed = serviceFee.toPercent(rate).toFixed(decimals);
        const trimmed = fixed.replace(/\.?0+$/, '') || '0';

        return `${trimmed.replace('.', ',')}%`;
    },

    /**
     * Taxa por unidade no meio de pagamento escolhido.
     *
     * Aceita um lote ou um produto: no carrinho, ingresso entra como lote e
     * produto entra inteiro, então cai no lote ativo dele.
     *
     * `service_fees` é o campo novo, com os dois meios. `service_fee` continua
     * existindo e continua sendo o valor de cartão — é o fallback para quando a
     * resposta ainda vier de uma versão anterior da API.
     */
    perUnit: (item, method = CREDIT_CARD, absorbed = false) => {
        if (!item || absorbed) {
            return 0;
        }

        const batches = item.batches || [];
        const hasOwnFee = item.service_fees !== undefined || item.service_fee !== undefined;
        const source = hasOwnFee ? item : (batches.find((batch) => batch.is_active) || batches[0]);

        if (!source) {
            return 0;
        }

        const fees = source.service_fees;
        const value = fees && fees[method] !== undefined ? fees[method] : source.service_fee;

        return Number(value) || 0;
    },

    /**
     * Taxa de um ingresso a esse preço — a mesma conta do `ServiceFeeCalculator`
     * do backend: abaixo do limiar, o mínimo fixo; a partir dele, a porcentagem
     * pura, mesmo que dê menos que o mínimo — é um corte deliberado, não onde a
     * % ultrapassa o mínimo.
     *
     * Taxa zerada não cobra nada. O mínimo é um piso para uma taxa que existe,
     * não uma cobrança própria: evento negociado sem taxa fica sem taxa.
     */
    forPrice: (price, rate, minimum, threshold = FLAT_FEE_THRESHOLD) => {
        if (price <= 0 || rate <= 0) {
            return 0;
        }

        if (price < threshold) {
            return minimum;
        }

        return price * rate;
    },

    /**
     * O caminho inverso: quanto cobrar para o produtor receber o líquido que
     * pediu. Abaixo do limiar, a taxa é uma soma fixa; a partir dele, sai da
     * inversão da porcentagem. Entre as duas faixas há um vão de líquidos
     * inatingíveis — bem no limiar, o líquido salta pra cima — e o limiar é o
     * menor preço que entrega pelo menos o que foi pedido.
     */
    priceFromNet: (desiredNet, rate, minimum, threshold = FLAT_FEE_THRESHOLD) => {
        if (desiredNet <= 0) {
            return 0;
        }

        const priceInFlatZone = desiredNet + minimum;

        if (priceInFlatZone < threshold) {
            return Math.ceil(priceInFlatZone * 100) / 100;
        }

        if (rate <= 0) {
            return Math.ceil(priceInFlatZone * 100) / 100;
        }

        const priceInPercentZone = desiredNet / (1 - rate);

        if (priceInPercentZone >= threshold) {
            return Math.ceil(priceInPercentZone * 100) / 100;
        }

        return threshold;
    },
};

export default serviceFee;
